import copy from 'copy-to-clipboard'
import Handsontable from 'handsontable'
import { isEqual, isObject, isUndefined } from 'lodash'
import SheetClipCls from 'sheetclip'
import { DataContainer } from 'dataContainer/data-container'
import { ignoredGovernanceTypes } from 'handsontable/config'
import { ADMIN_ROUTES, ROUTES } from 'routes'
import { CellProperties } from './types'
import { DatasetWithDatasetRows } from '@global/types'
import { CellValue, RowObject } from 'handsontable/common'
import { GridSettings } from 'handsontable/settings'
import { t } from '@lingui/core/macro'

const SheetClip = new SheetClipCls()

interface Data {
  cvid: string
  cell_value: string
  field_id: string
  metadata: {
    isVisible: boolean
  }
  originalValue: string
  readOnly: boolean
  source: unknown[]
  validationErrors: unknown[]
  warnings: unknown[]
}
interface ContextArgs {
  isResubmit: boolean
  dataContainer: DataContainer
  setCopyStatus?: (status: boolean) => void
  isAdmin: boolean
}

/**
 * Returns the right-click context menu for the Handsontable.
 * https://handsontable.com/docs/8.3.2/Options.html#contextMenu
 * @param {boolean} isResubmit - Editing data for a previously completed submission.
 * @return {ContextMenu} Context menu
 */
export function getContextMenu({
  isResubmit,
  dataContainer,
  setCopyStatus,
  isAdmin,
}: ContextArgs): GridSettings['contextMenu'] {
  if (isResubmit) {
    return {
      items: {
        copy_cell_value: {
          name: t`Copy cell value`,
          /**
           * Select the cell values based on the selected range of columns and rows.
           * @param options The selection coords.
           */
          callback: (_, options) => {
            if (!options[0]) {
              return
            }

            const { end, start } = options[0]
            const { row: startRow, col: startCol } = start
            const { row: endRow, col: endCol } = end
            const copyOptions = { format: 'text/plain' }
            const dataContainerData: Data[] = dataContainer.data
            const dataVisibleColumns = dataContainerData.map(d => {
              const columns = Object.values(d)
              const visibleColumns = columns.filter(d => d?.metadata.isVisible)
              return visibleColumns
            })

            const isSingleCellSelected = startRow === endRow && startCol === endCol
            const isSingleRowColsSelected = startRow === endRow && startCol !== endCol

            if (isSingleCellSelected) {
              const matchingRow = dataVisibleColumns[startRow]

              if (!matchingRow) {
                return
              }

              const key = Object.keys(matchingRow)[startCol]
              const rowWithSimpleIndex = matchingRow ? (key ? matchingRow[key] : null) : null
              if (rowWithSimpleIndex) {
                copy(rowWithSimpleIndex?.cell_value, copyOptions)
              }

              setCopyStatus?.(true)

              return
            }

            if (isSingleRowColsSelected) {
              const matchingRow = dataVisibleColumns[startRow]
              if (!matchingRow) {
                return
              }

              const cellValues = [
                matchingRow.slice(startCol, endCol + 1).map(col => col?.cell_value),
              ]
              const friendlyToExcel = SheetClip.stringify(cellValues)

              copy(friendlyToExcel, copyOptions)

              setCopyStatus?.(true)

              return
            }

            const dataWithinRange = dataVisibleColumns.slice(startRow, endRow + 1)
            const cellValues = dataWithinRange.map(row => {
              const onlyColumnsInSelection = row.slice(startCol, endCol + 1)
              return onlyColumnsInSelection.map(col => col?.cell_value)
            })
            const friendlyToExcel = SheetClip.stringify(cellValues)

            copy(friendlyToExcel, copyOptions)

            setCopyStatus?.(true)
          },
        },
      },
    }
  }

  return {
    items: {
      row_above: {
        name: t`Insert row above`,
      },
      row_below: {
        name: t`Insert row below`,
      },
      remove_row: {
        name: t`Remove row`,
      },
      admin_jump_to_edit: {
        name: t`Edit from Template Edit`,
        hidden: () => !isAdmin,
        disabled(this: Handsontable) {
          const selected = this.getSelected()
          if (!selected?.[0]) return true

          const [, col] = selected[0]
          const templateField = getTemplateFieldFromColumnIndex(dataContainer, col)
          return !templateField?.template_id && !templateField?.field?.id
        },
        callback: (_, options) => {
          if (!options[0]) {
            return
          }
          const { col } = options[0].start
          const templateField = getTemplateFieldFromColumnIndex(dataContainer, col)
          const templateId = templateField?.template_id
            ? templateField?.template_id
            : dataContainer.templateFields.find(tf => tf.template_id)?.template_id

          const fieldId = templateField?.field?.id

          window.open(
            `${ROUTES.ADMIN_TEMPLATE_EDIT_GOVERN.replace(':id', templateId)}${fieldId ? `?open=${fieldId}` : ''}`,
            '_blank',
          )
        },
      },
      admin_jump_to_field_set: {
        name: t`Edit from Field Set`,
        hidden: () => !isAdmin,
        disabled(this: Handsontable) {
          const selected = this.getSelected()
          if (!selected?.[0]) return true

          const [, col] = selected[0]
          const templateField = getTemplateFieldFromColumnIndex(dataContainer, col)
          return !templateField?.template_id && !templateField?.field?.id
        },
        callback: (_, options) => {
          if (!options[0]) {
            return
          }
          const { col } = options[0].start
          const templateField = getTemplateFieldFromColumnIndex(dataContainer, col)
          const fieldSetId = templateField?.field?.fieldset_id
          const fieldId = templateField?.field?.id

          window.open(
            `${ADMIN_ROUTES.FIELDSET_FIELDS.replace(':id', fieldSetId)}${fieldId ? `?open=${fieldId}` : ''}`,
            '_blank',
          )
        },
      },
    },
  }
}

/**
 * Returns the template field from the column index.
 * We need to filter out the template fields that are not visible on the form
 * to match the visible columns in the Handsontable.
 * @param {DataContainer} dataContainer
 * @param {number} col
 * @returns {object} Template field
 */
function getTemplateFieldFromColumnIndex(dataContainer: DataContainer, col: number) {
  const visibleTemplateFields = dataContainer.templateFields.filter(tf => tf.visible_on_form)
  return visibleTemplateFields[col]
}

/**
 * Returns the maxium number of rows to show in the Handsontable.
 * The data will be trimmed to the provided number of rows.
 * - If editing data from a previous submission, more rows cannot be added.
 * - For new submissions, the maximum number of rows is 3000.
 * @param {object[]} dataContainerData
 * @param {DatasetWithDatasetValue} dataset
 * @param {boolean} isResubmit
 * @returns {number} Maximum rows
 */
export function getMaxRows(
  dataContainerData: object[],
  dataset: DatasetWithDatasetRows | null,
  isResubmit: boolean,
): number {
  const defaultRowCount = 3000
  const existingDataRows = dataset?.dataset_rows || []

  if (isResubmit && existingDataRows) {
    return existingDataRows.length
  }

  if (dataContainerData.length > defaultRowCount) {
    return dataContainerData.length
  }

  return defaultRowCount
}

/**
 * Returns the data for Handsontable, excluding rows that have not been fully processed.
 * @param {object} dataContainer
 * @returns Data for Handsontable
 */
export function getDataForHotTable(dataContainer: DataContainer): CellValue[][] | RowObject[] {
  if (dataContainer.pendingRows.size > 0) {
    return dataContainer.data.filter(rowIsNotPending)
  }

  return dataContainer.data

  function rowIsNotPending(row: object): boolean {
    return Object.values(row).some(({ cvid }) => !dataContainer.pendingRows.has(cvid))
  }
}

/**
 * Updates a cell to read only if its metadata includes readOnly property.
 * If template has Branch integration, and a deep link exist in the same row,
 * this function also updates the alias to readOnly.
 * https://handsontable.com/docs/8.3.2/Options.html#cells
 * @param {number} row
 * @param {string} prop
 * @param {object} hotTableData
 * @param {object} branchIntegration
 * @returns {CellProperties} an object that includes cell's updated properties
 */
export interface BranchIntegrationObj {
  alias: string
  deepLinkId: number
  datasetRows: any
}
export function updateCellsToReadOnly(
  column: {
    editor: unknown
  } | null,
  row: number,
  prop: string,
  hotTableData: object,
  branchIntegrationObj: BranchIntegrationObj | null,
): CellProperties {
  const cellProperties: CellProperties = {}

  if (column && column?.editor === null) {
    return { readOnly: true }
  }

  if (typeof prop === 'string' && prop.includes('.cell_value')) {
    const fieldId = prop.split('.cell_value')[0]
    const cellData = hotTableData[row] ? hotTableData[row][fieldId] : null

    cellProperties.readOnly = Boolean(cellData?.metadata?.readOnly)

    if (branchIntegrationObj) {
      const { alias, datasetRows, deepLinkId } = branchIntegrationObj

      if (datasetRows?.[row]) {
        const deepLinkExists = branchDeepLinkExists(deepLinkId, datasetRows[row].dataset_values)

        if (deepLinkExists && alias === fieldId) {
          cellProperties.readOnly = true
        }
      }
    }
  }

  return cellProperties
}

/**
 * Checks whether a Branch deep link exists in a given submission row.
 * @param {number} deepLinkId
 * @param {any} datasetRows
 * @returns {boolean}
 */
function branchDeepLinkExists(deepLinkId: number, datasetRows: any): boolean {
  return (
    datasetRows.filter(
      row =>
        row.field_id == deepLinkId &&
        row.cell_value !== 'BRANCH_LINK_ALIAS_CONFLICT' &&
        row.cell_value !== 'BRANCH_SUBMISSION_ERROR',
    ).length > 0
  )
}

/**
 * Checks if all cells in the given row are empty.
 * https://handsontable.com/docs/8.3.2/Core.html#isEmptyRow
 * @param {number} rowNum
 * @param {Handsontable} hotTable
 * @returns {boolean} true if the row at the given row is empty, false otherwise
 */
export function customIsEmptyRow(rowNum: number, hotTable: Handsontable): boolean {
  if (!hotTable) return true

  const numCols = hotTable.countCols()

  for (let col = 0; col < numCols; col++) {
    const value = hotTable.getDataAtCell(rowNum, col)

    if (value !== '' && value !== null && !isUndefined(value)) {
      const meta = hotTable.getCellMeta(rowNum, col)
      const field = meta.field || {}
      const gov = field.segment_type === 'variable' ? meta : field
      if (ignoredGovernanceTypes[gov.governance_type]) {
        continue
      } else if (gov.governance_type === 'link' && meta?.__proto__.autofill) {
        continue
      } else if (isObject(value)) {
        return isEqual(hotTable.getSchema[meta.prop], value)
      }
      return false
    }
  }
  return true
}

/**
 * Returns the coordinates for the target cell when pressing the 'TAB' key.
 * @param {KeyboardEvent} event
 * @param {Handsontable} hotTable
 * @returns {{row: number, col: number}} an object with the coordinates of the target cell
 */
export function specifyTabMove(
  event: KeyboardEvent,
  hotTable: Handsontable,
): {
  row: number
  col: number
} {
  const selected = hotTable.getSelected()
  const countCols = hotTable.countCols()
  if (selected?.[0]) {
    const selectedStart = selected[0]
    // if we are on the end and tabbing
    if (!event.shiftKey && selectedStart[1] === countCols - 1) {
      return { row: 1, col: -(countCols - 1) }
    }
    // if we are on the first and going backwards
    if (event.shiftKey && selectedStart[1] === 0) {
      return { row: 1, col: -(countCols - 1) }
    }
  }

  return { row: 0, col: 1 }
}

export function checkLockedStatus(column) {
  const isVariable = column.field.segment_type === 'variable'
  const governanceOwner = isVariable ? column : column.field
  const { dependsOnSibling } = column
  const isPattern = governanceOwner.is_pattern
  const isConcat = column.field.governance_type === 'concatenation'
  const isAutofill = governanceOwner.governance_type === 'autofill'
  const isConstant = governanceOwner.governance_type === 'constant'
  const isAutoPopulatedDate = governanceOwner.date?.auto_populate
  const isCvId = column.governance_type === 'cv_id'

  const isList = governanceOwner.governance_type === 'list'
  const isNotFirstColumn =
    governanceOwner.list_governance?.list_column &&
    governanceOwner.list_governance.list_column.precedence > 0
  const isNonFirstColumnOfDependentColumn =
    isList &&
    isNotFirstColumn &&
    governanceOwner.list_governance?.lists &&
    governanceOwner.list_governance.lists.type === 'dependent'

  return (
    column.field.governance_type === 'shortened link' ||
    column.field.governance_type === 'qr-code' ||
    column.field.governance_type === 'deep-link' ||
    column.field.governance_type === 'custom-bitlink' ||
    column.readOnly ||
    column.renderOnly ||
    !column.editor ||
    isConstant ||
    isConcat ||
    dependsOnSibling ||
    isPattern ||
    isAutofill ||
    isNonFirstColumnOfDependentColumn ||
    isAutoPopulatedDate ||
    isCvId
  )
}

/**
 * Checks if all rows in the table are empty - needed to disabled the "Submit" button
 *
 * @param hotTable Handsontable intance
 * @param setTableHasData Updates the state in CreateDataset to disable/enable the "Submit" button
 * @returns
 */
export function checkIfTableHasData(hotTable: Handsontable, setTableHasData) {
  const emptyRows = hotTable?.countEmptyRows()
  const totalRows = hotTable?.countRows()

  if (emptyRows === 0) {
    setTableHasData(true)
  } else {
    setTableHasData(emptyRows !== totalRows)
  }
}
